import { useEffect, useState } from 'react';

/** The delay every admin search box uses. One number, one place. */
export const SEARCH_DEBOUNCE_MS = 350;

/**
 * The trailing-edge debounce behind the admin search boxes.
 *
 * Every people-search surface (Users, Inactive Users, an organisation's
 * Members) re-fetches from the SERVER as you type, so each keystroke would
 * otherwise be a request. This returns the value only once it has stopped
 * changing for `delayMs`.
 *
 * Extracted because the same two-state + `setTimeout` effect had been written
 * out at three call sites, which meant the delay and the trimming could drift
 * apart between surfaces.
 *
 * Strings are trimmed, so a term of only spaces settles to `''` (i.e. "no
 * search") rather than a query the server has to special-case.
 */
export function useDebounce<T>(value: T, delayMs: number = SEARCH_DEBOUNCE_MS): T {
  const normalise = (v: T): T => (typeof v === 'string' ? (v.trim() as unknown as T) : v);
  const [debounced, setDebounced] = useState<T>(() => normalise(value));

  useEffect(() => {
    const id = setTimeout(() => setDebounced(normalise(value)), delayMs);
    return () => clearTimeout(id);
    // `normalise` is pure and stable in behaviour; re-running on value/delay is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, delayMs]);

  return debounced;
}
